/**
 * Derived-stat engine. Imported by both the API and the UI so a bonus shown
 * on screen is computed by exactly the same code that validates it server-side.
 */
import {
  ABILITIES, CLASSES, FULL_CASTER_SLOTS, PACT_SLOTS, SKILLS, SKILL_KEYS, XP_THRESHOLDS,
  type Ability, type ClassDef, type ClassKey, type ProficiencyLevel, type SkillKey,
} from './constants'

export interface AbilityScores { str: number; dex: number; con: number; int: number; wis: number; cha: number }

export interface CalcInput {
  level: number
  class: string
  scores: AbilityScores
  saveProficiencies: Ability[]
  skillProficiencies: Partial<Record<SkillKey, ProficiencyLevel>>
  armorClass: number
  acIsManual: boolean
  initiativeBonus: number
}

export const abilityModifier = (score: number): number => Math.floor((score - 10) / 2)

export const proficiencyBonus = (level: number): number =>
  2 + Math.floor((clampLevel(level) - 1) / 4)

export const clampLevel = (level: number): number =>
  Math.min(20, Math.max(1, Math.trunc(level) || 1))

/** "+3" / "-1" / "+0" — modifiers always carry a sign in 5e. */
export const formatModifier = (value: number): string => (value < 0 ? `${value}` : `+${value}`)

export const classDef = (key: string): ClassDef | null => CLASSES[key as ClassKey] ?? null

export function abilityModifiers(scores: AbilityScores): Record<Ability, number> {
  return Object.fromEntries(
    ABILITIES.map((a) => [a, abilityModifier(scores[a])]),
  ) as Record<Ability, number>
}

export function saveBonus(input: CalcInput, ability: Ability): number {
  const proficient = input.saveProficiencies.includes(ability)
  return abilityModifier(input.scores[ability]) + (proficient ? proficiencyBonus(input.level) : 0)
}

export function skillBonus(input: CalcInput, skill: SkillKey): number {
  const { ability } = SKILLS[skill]
  const level = input.skillProficiencies[skill]
  const pb = proficiencyBonus(input.level)
  const bonus = level === 'expertise' ? pb * 2 : level === 'proficient' ? pb : 0
  return abilityModifier(input.scores[ability]) + bonus
}

export function passivePerception(input: CalcInput): number {
  return 10 + skillBonus(input, 'perception')
}

/** Unarmored default; a manual AC (armor, shields, Mage Armor) overrides it. */
export function effectiveArmorClass(input: CalcInput): number {
  return input.acIsManual ? input.armorClass : 10 + abilityModifier(input.scores.dex)
}

export function initiative(input: CalcInput): number {
  return abilityModifier(input.scores.dex) + input.initiativeBonus
}

export interface SpellcastingSummary {
  ability: Ability
  modifier: number
  saveDC: number
  attackBonus: number
  /** 1-indexed by spell level: slots[3] = number of 3rd-level slots. */
  slots: Record<number, number>
  pactSlotLevel: number | null
}

export function spellcasting(input: CalcInput): SpellcastingSummary | null {
  const def = classDef(input.class)
  if (!def || !def.spellcastingAbility) return null

  const ability = def.spellcastingAbility
  const modifier = abilityModifier(input.scores[ability])
  const pb = proficiencyBonus(input.level)
  const level = clampLevel(input.level)

  const slots: Record<number, number> = {}
  let pactSlotLevel: number | null = null

  if (def.casting === 'pact') {
    const entry = PACT_SLOTS[level - 1]
    if (entry) {
      slots[entry[1]] = entry[0]
      pactSlotLevel = entry[1]
    }
  } else {
    // Half-casters gain slots at half rate (rounded up); third-casters at a third.
    const divisor = def.casting === 'half' ? 2 : def.casting === 'third' ? 3 : 1
    const effective = divisor === 1 ? level : Math.ceil(level / divisor)
    const row = FULL_CASTER_SLOTS[Math.max(0, effective - 1)]
    row?.forEach((count, i) => { if (count > 0) slots[i + 1] = count })
  }

  return { ability, modifier, saveDC: 8 + pb + modifier, attackBonus: pb + modifier, slots, pactSlotLevel }
}

export function levelFromXp(xp: number): number {
  let level = 1
  for (let i = 0; i < XP_THRESHOLDS.length; i++) {
    if (xp >= XP_THRESHOLDS[i]!) level = i + 1
  }
  return level
}

export function xpToNextLevel(xp: number): { next: number | null; remaining: number; progress: number } {
  const level = levelFromXp(xp)
  if (level >= 20) return { next: null, remaining: 0, progress: 1 }
  const floor = XP_THRESHOLDS[level - 1]!
  const next = XP_THRESHOLDS[level]!
  return { next, remaining: next - xp, progress: (xp - floor) / (next - floor) }
}

/** Average HP a class gains per level after 1st: (hitDie / 2) + 1, plus CON. */
export function averageHitPoints(classKey: string, level: number, conModifier: number): number {
  const def = classDef(classKey)
  if (!def) return 0
  const lv = clampLevel(level)
  const perLevel = def.hitDie / 2 + 1
  return def.hitDie + conModifier + (lv - 1) * (perLevel + conModifier)
}

export interface DerivedStats {
  proficiencyBonus: number
  modifiers: Record<Ability, number>
  saves: Record<Ability, number>
  skills: Record<SkillKey, number>
  passivePerception: number
  armorClass: number
  initiative: number
  spellcasting: SpellcastingSummary | null
  hitDie: number | null
}

export function deriveStats(input: CalcInput): DerivedStats {
  const def = classDef(input.class)
  return {
    proficiencyBonus: proficiencyBonus(input.level),
    modifiers: abilityModifiers(input.scores),
    saves: Object.fromEntries(ABILITIES.map((a) => [a, saveBonus(input, a)])) as Record<Ability, number>,
    skills: Object.fromEntries(SKILL_KEYS.map((s) => [s, skillBonus(input, s)])) as Record<SkillKey, number>,
    passivePerception: passivePerception(input),
    armorClass: effectiveArmorClass(input),
    initiative: initiative(input),
    spellcasting: spellcasting(input),
    hitDie: def?.hitDie ?? null,
  }
}
